// frontend/src/pages/admin/ManagePlayers.js
import React, { useEffect, useState, useContext, useMemo, useRef } from "react";
import API from "../../api/api";
import { resolveAssetUrl } from "../../api/config";
import { AuthContext } from "../../context/AuthContext";
import Navbar from "../../components/Navbar";

// Common reusable components
import { validateAadhaar } from "../../components/common/AadhaarValidation";
import PDFViewer from "../../components/common/PDFViewer";
import Modal from "../../components/common/Modal";
import SearchBar from "../../components/common/SearchBar";
import DataTable from "../../components/common/DataTable";
import ExportButtons from "../../components/common/ExportButtons";
import { successAlert,errorAlert,confirmAlert} from "../../utils/alert";
import Swal from "sweetalert2";
import SearchableSelect from "../../components/common/SearchableSelect";

const norm = (v) => String(v || "").trim().toLowerCase();
const toBool = (v) => {
  if (v === true || v === 1) return true;
  const raw = String(v ?? "").trim().toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
};

const ManagePlayers = () => {
  const { role } = useContext(AuthContext);

  const emptyForm = {
    name: "",
    relation: "",
    dob: "",
    gender: "",
    aadhaar_number: "",
    mobile: "",
    email: "",
    district: "",
    mandal: "",
    postoffice: "",
    address: "",
    state: "",
    pincode: "",
    club: "",
    coach: "",
  };

  const [emailError, setEmailError] = useState("");
  const [aadhaarError, setAadhaarError] = useState("");
  const [players, setPlayers] = useState([]);
  const [formData, setFormData] = useState(emptyForm);
  const [fileData, setFileData] = useState({});
  const [editing, setEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewPlayer, setViewPlayer] = useState(null);
  const [viewFile, setViewFile] = useState(null);
  const [existingFiles, setExistingFiles] = useState({});
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [selectedPostOffice, setSelectedPostOffice] = useState("");
  const mandalOptions = useMemo(
    () =>
      [...new Set(pincodeOptions.map((opt) => (opt.mandal || "").trim()))].filter(Boolean),
    [pincodeOptions]
  );
  const districtOptions = useMemo(
    () =>
      [...new Set(pincodeOptions.map((opt) => (opt.district || "").trim()))].filter(Boolean),
    [pincodeOptions]
  );
  const villageRef = useRef(null);

  // Pagination & Search
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const limit = 10;
  const [totalPages, setTotalPages] = useState(1);

  // === FETCH PLAYERS ===
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        const res = await API.get("/admin/players", {
          params: { page, limit, search: searchTerm },
        });

        setPlayers(res.data || []);
        setTotalPages(res.last_page);
      } catch (err) {
        console.error("Error loading players:", err);
        setPlayers([]);
      }
    };

    fetchPlayers();
  }, [page, limit, searchTerm]);

  // Reset pagination only when search text changes
  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  // === HANDLERS ===
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clear server-side errors when user edits
    if (name === "aadhaar_number") {
      setAadhaarError("");
    }
    if (name === "email") {
      setEmailError("");
    }

    if (name === "aadhaar_number") {
      const cleaned = value.replace(/\D/g, "").slice(0, 12);
      setFormData({ ...formData, aadhaar_number: cleaned });

      if (cleaned.length === 12 && !validateAadhaar(cleaned)) {
        setAadhaarError("❌ Invalid Aadhaar number (checksum failed)");
      }
    } 
    else if (name === "email") {
      setFormData({ ...formData, email: value });

      if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
        setEmailError("❌ Invalid email format");
      }
    } 
    else {
      if (name === "pincode") {
        const clean = value.replace(/\D/g, "").slice(0, 6);
        setFormData({
          ...formData,
          pincode: clean,
          mandal: "",
          district: "",
          postoffice: "",
        });
        setSelectedPostOffice("");
        setPincodeOptions([]);
      } else {
        setFormData({ ...formData, [name]: value });
      }
    }
  };

  useEffect(() => {
    const pincode = String(formData.pincode || "").trim();
    if (pincode.length !== 6) {
      setPincodeOptions([]);
      setSelectedPostOffice("");
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const rows = await API.lookupPincode(pincode);
        const list = Array.isArray(rows) ? rows : [];
        setPincodeOptions(list);
        if (list.length > 0) {
          setTimeout(() => villageRef.current?.focus(), 0);
        }

        const pickBest = () => {
          if (list.length === 0) return null;
          if (list.length === 1) return list[0];
          return (
            list.find((x) => norm(x.postoffice) === norm(formData.postoffice)) ||
            list.find(
              (x) =>
                norm(x.mandal) === norm(formData.mandal) &&
                norm(x.district) === norm(formData.district)
            ) ||
            list.find((x) => norm(x.district) === norm(formData.district)) ||
            list.find((x) => norm(x.mandal) === norm(formData.mandal)) ||
            list[0]
          );
        };

        const best = pickBest();
        setSelectedPostOffice((prev) => {
          if (prev && list.some((x) => norm(x.postoffice) === norm(prev))) {
            return prev;
          }
          return best?.postoffice || "";
        });
        if (best) {
          setFormData((prev) => ({
            ...prev,
            postoffice: prev.postoffice || best.postoffice || "",
            mandal: prev.mandal || best.mandal || "",
            district: prev.district || best.district || "",
            state: prev.state || best.state || "",
          }));
        }
      } catch (err) {
        setPincodeOptions([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.pincode, formData.postoffice, formData.mandal, formData.district]);

  const handleFileChange = (e) =>
    setFileData({ ...fileData, [e.target.name]: e.target.files[0] });

  const handleAadhaarBlur = async () => {
    const aadhaar = formData.aadhaar_number?.trim();

    if (!aadhaar || aadhaar.length !== 12) {
      setAadhaarError("");
      return;
    }

    if (!validateAadhaar(aadhaar)) {
      setAadhaarError("❌ Invalid Aadhaar number (checksum failed)");
      return;
    }

    try {
      const res = await API.get("/auth/check-aadhaar", {
        params: {
          aadhaar,
          ignore: editing ? editId : null,
        },
      });

      // ✅ res IS ALREADY DATA
      if (res.invalid) {
        setAadhaarError("❌ Invalid Aadhaar number");
      } else if (res.exists) {
        setAadhaarError("❌ Aadhaar number already exists");
      } else {
        setAadhaarError("");
      }
    } catch (e) {
      console.error("Aadhaar check failed:", e);
      setAadhaarError("❌ Unable to validate Aadhaar");
    }
  };

  const handleEmailBlur = async () => {
    const email = formData.email?.trim();

    if (!email) {
      setEmailError("");
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError("❌ Invalid email format");
      return;
    }

    try {
      const res = await API.get("/auth/check-email", {
        params: {
          email,
          ignore: editing ? editId : null,
        },
      });

      // ✅ res IS ALREADY DATA
      if (res.exists) {
        setEmailError("❌ Email already exists");
      } else {
        setEmailError("");
      }
    } catch (e) {
      console.error("Email check failed:", e);
      setEmailError("❌ Unable to validate Email");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear previous server errors
    setEmailError("");
    setAadhaarError("");

    if (aadhaarError) {
      errorAlert("Aadhaar Validation Error", "Please Check Aadhaar Number");
      return;
    }

    if (emailError) {
      errorAlert("Email Validation Error", "Please Check Email");
      return;
    }

    if (!validateAadhaar(formData.aadhaar_number)) {
      errorAlert("Invalid Aadhaar", "Checksum validation failed");
      return;
    }

    const form = new FormData();
    Object.entries(formData).forEach(([k, v]) => form.append(k, v));
    Object.entries(fileData).forEach(([k, f]) => f && form.append(k, f));

    try {
      if (editing) {
        const confirmed = await confirmAlert({
          title: "Update Player?",
          text: "Do you want to save changes?",
          confirmText: "Update",
        });

        if (!confirmed) return;

        form.append("_method", "PUT");
        await API.post(`/admin/players/${editId}`, form);
        successAlert("Updated!", "Player updated successfully");
      } else {
        const confirmed = await confirmAlert({
          title: "Add Player?",
          text: "Do you want to add this player?",
          confirmText: "Add",
        });

        if (!confirmed) return;

        await API.post("/admin/players", form);
        successAlert("Added!", "Player added successfully");
      }

      setFormData(emptyForm);
      setFileData({});
      setPincodeOptions([]);
      setSelectedPostOffice("");
      setEditing(false);
      setEditId(null);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });

      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      } catch (err) {
        console.log("Full error:", err);

        const serverErrors = err?.response?.data?.errors;

        if (serverErrors) {
          // Aadhaar duplicate / invalid
          if (serverErrors.aadhaar_number?.[0]) {
            setAadhaarError(`❌ ${serverErrors.aadhaar_number[0]}`);
          }

          // Email duplicate
          if (serverErrors.email?.[0]) {
            setEmailError(`❌ ${serverErrors.email[0]}`);
          }
        }

        const errMsg = serverErrors
          ? Object.values(serverErrors).flat().join(", ")
          : err?.response?.data?.message || "Server error";

        errorAlert("Save Failed", errMsg);
      }
  };

  const handleEdit = (player) => {
    window.scrollTo({ top: 0, behavior: "smooth" });

    setFormData({
      ...emptyForm,
      name: player.name || "",
      relation: player.relation || "",
      dob: player.dob ? new Date(player.dob).toISOString().split("T")[0] : "",
      gender: player.gender || "",
      aadhaar_number: player.aadhaar_number || "",
      mobile: player.mobile || "",
      email: player.email || "",
      district: player.district || "",
      mandal: player.mandal || "",
      postoffice: player.postoffice || "",
      address: player.address || "",
      state: player.state || "",
      pincode: player.pincode || "",
      club: player.club || "",
      coach: player.coach || "",
    });

    // ✅ STORE EXISTING FILE PATHS SEPARATELY
    setExistingFiles({
      photo: player.photo || null,
      aadhaar: player.aadhaar || null,
      birth_certificate: player.birth_certificate || null,
      sadarem: player.sadarem || null,
      others: player.others || null,
    });

    setFileData({}); // reset new uploads
    setSelectedPostOffice(player.postoffice || "");
    setEditId(player.id);
    setEditing(true);
  };

  const handleDelete = async (id) => {
    const confirmed = await confirmAlert({
      title: "Delete Player?",
      text: "This will permanently delete the player",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      await API.delete(`/admin/players/${id}`);
      setPlayers(players.filter((p) => p.id !== id));
      successAlert("Deleted", "Player deleted successfully");
    } catch {
      errorAlert("Error", "Failed to delete player");
    }
  };

  const handleResetPlayerPassword = async (player) => {
    const result = await Swal.fire({
      title: `Reset password for ${player.name}?`,
      input: "password",
      inputLabel: "Enter new password",
      inputPlaceholder: "New password (min 6 chars)",
      inputAttributes: { autocapitalize: "off", autocorrect: "off" },
      showCancelButton: true,
      confirmButtonText: "Reset",
      inputValidator: (value) => {
        if (!value || value.length < 6) return "Password must be at least 6 characters";
        return null;
      },
    });

    if (!result.isConfirmed) return;

    try {
      await API.resetPlayerPassword(player.id, result.value);
      successAlert("Password reset", "Player password updated successfully");
    } catch (err) {
      errorAlert("Reset failed", err?.message || "Failed to reset player password");
    }
  };

  const handleApprovalStatus = async (player, approvalStatus) => {
    const confirmed = await confirmAlert({
      title: `${approvalStatus} player?`,
      text: `${player.name} will be marked as ${approvalStatus}.`,
      confirmText: approvalStatus,
    });
    if (!confirmed) return;

    try {
      await API.put(`/admin/players/${player.id}/approval-status`, {
        approval_status: approvalStatus,
      });

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", `Player marked as ${approvalStatus}`);
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update approval status");
    }
  };

  const handleBlockStatus = async (player, shouldBlock) => {
    let blockedReason = "";

    if (shouldBlock) {
      const prompt = await Swal.fire({
        title: `Block ${player.name}?`,
        input: "text",
        inputLabel: "Reason (optional)",
        inputPlaceholder: "Reason for blocking login",
        showCancelButton: true,
        confirmButtonText: "Block",
      });

      if (!prompt.isConfirmed) return;
      blockedReason = String(prompt.value || "").trim();
    } else {
      const confirmed = await confirmAlert({
        title: `Release ${player.name}?`,
        text: "Player will be allowed to login again.",
        confirmText: "Release",
      });
      if (!confirmed) return;
    }

    try {
      await API.updatePlayerBlockStatus(player.id, shouldBlock, blockedReason);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", shouldBlock ? "Player blocked successfully" : "Player released successfully");
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update block status");
    }
  };

  const handleFeeExemptionStatus = async (player, shouldExempt) => {
    const confirmed = await confirmAlert({
      title: shouldExempt ? "Grant fee exemption?" : "Remove fee exemption?",
      text: shouldExempt
        ? `${player.name} will be exempted from event registration and escort fees.`
        : `${player.name} will be charged regular event registration and escort fees.`,
      confirmText: shouldExempt ? "Grant" : "Remove",
    });
    if (!confirmed) return;

    try {
      await API.updatePlayerFeeExemption(player.id, shouldExempt);

      const res = await API.get("/admin/players", {
        params: { page, limit, search: searchTerm },
      });
      setPlayers(res.data || []);
      setTotalPages(res.last_page || 1);
      successAlert("Updated", shouldExempt ? "Fee exemption granted" : "Fee exemption removed");
    } catch (err) {
      errorAlert("Update failed", err?.message || "Failed to update fee exemption");
    }
  };

  const handleFileDelete = async (id, field) => {
    const confirmed = await confirmAlert({
      title: `Delete ${field}?`,
      text: "This file will be removed permanently",
      confirmText: "Delete",
    });

    if (!confirmed) return;

    try {
      await API.delete(`/admin/players/${id}/file/${field}`);

      // ✅ REMOVE FILE FROM UI STATE IMMEDIATELY
      setExistingFiles((prev) => ({
        ...prev,
        [field]: null,
      }));

      setViewPlayer((prev) =>
        prev ? { ...prev, [field]: null } : prev
      );

      successAlert("Deleted", `${field} deleted successfully`);
    } catch (err) {
      errorAlert("Error", "Failed to delete file");
    }
  };

  // === SEARCH & PAGINATION ===
  // const filteredPlayers = useMemo(() => {
  //   const term = searchTerm.toLowerCase().trim();
  //   const skipFields = ["photo", "aadhaar", "birth_certificate", "sadarem", "others", "password"];

  //   return (players || []).filter((p) =>
  //     Object.keys(p).some((key) => {
  //       if (skipFields.includes(key)) return false;
  //       const value = p[key];
  //       return value && value.toString().toLowerCase().includes(term);
  //     })
  //   );
  // }, [players, searchTerm]);

  // === TABLE CONFIG ===
  const columns = [
    {
      header: "Photo",
      render: (p) =>
        p.photo ? (
          <img
            src={resolveAssetUrl(p.photo)}
            alt="Player"
            className="w-10 h-10 rounded-full object-cover mx-auto border"
          />
        ) : (
          "-"
        ),
    },
    { header: "Name", accessor: "name" },
    { header: "Gender", accessor: "gender" },
    { header: "Aadhaar", accessor: "aadhaar_number" },
    { header: "District", accessor: "district" },
    {
      header: "Approval",
      render: (p) => {
        const status = String(p.approval_status || "Approved");
        const color =
          status === "Approved"
            ? "bg-green-100 text-green-700"
            : status === "Rejected"
              ? "bg-red-100 text-red-700"
              : "bg-yellow-100 text-yellow-700";
        return (
          <span className={`px-2 py-0.5 rounded text-[11px] font-semibold ${color}`}>
            {status}
          </span>
        );
      },
    },
    {
      header: "Login",
      render: (p) => {
        const blocked = toBool(p.is_blocked);
        return (
          <span
            className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
              blocked ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
            }`}
          >
            {blocked ? "Blocked" : "Active"}
          </span>
        );
      },
    },
    {
      header: "Fee Exemption",
      render: (p) => {
        const exempt = toBool(p.is_fee_exempt);
        return (
          <span
            className={`px-2 py-0.5 rounded text-[11px] font-semibold ${
              exempt ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-700"
            }`}
          >
            {exempt ? "Exempted" : "Regular"}
          </span>
        );
      },
    },
    {
      header: "Actions",
      render: (p) => {
        const blocked = toBool(p.is_blocked);
        const exempt = toBool(p.is_fee_exempt);
        return (
        <div className="flex flex-nowrap gap-1.5 max-w-[560px] overflow-x-auto py-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <button
            onClick={() => setViewPlayer(p)}
            className="bg-green-600 hover:bg-green-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            View
          </button>
          <button
            onClick={() => handleEdit(p)}
            className="bg-yellow-500 hover:bg-yellow-600 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Edit
          </button>
          <button
            onClick={() => handleDelete(p.id)}
            className="bg-red-600 hover:bg-red-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Delete
          </button>
          <button
            onClick={() => handleResetPlayerPassword(p)}
            className="bg-blue-600 hover:bg-blue-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Reset Password
          </button>
          <button
            onClick={() => handleApprovalStatus(p, "Approved")}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Approve
          </button>
          <button
            onClick={() => handleApprovalStatus(p, "Rejected")}
            className="bg-orange-600 hover:bg-orange-700 text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap"
          >
            Reject
          </button>
          <button
            onClick={() => handleBlockStatus(p, !blocked)}
            className={`text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap ${
              blocked ? "bg-teal-600 hover:bg-teal-700" : "bg-rose-600 hover:bg-rose-700"
            }`}
          >
            {blocked ? "Release Login" : "Block Login"}
          </button>
          <button
            onClick={() => handleFeeExemptionStatus(p, !exempt)}
            className={`text-white text-[11px] px-2 py-1 rounded-md leading-none whitespace-nowrap ${
              exempt ? "bg-slate-600 hover:bg-slate-700" : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            {exempt ? "Remove Exemption" : "Grant Exemption"}
          </button>
        </div>
      );
      },
    },
  ];

  // === ROLE GUARD ===
  if (!role) return null;

  if (!["admin", "secretary", "district"].includes((role || "").toLowerCase())) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Unauthorized Access
      </div>
    );
  }

  // === UI ===
  return (
    <>
      <Navbar />
      <div className="max-w-6xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-cyan-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-cyan-700">Player Registry</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Manage Players</h1>
              <p className="text-sm text-slate-600 mt-1">
                Maintain master player records, document verification, approvals and access controls.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Records</p>
                <p className="text-lg font-semibold text-slate-900">{players.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Page</p>
                <p className="text-lg font-semibold text-slate-900">{page}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Edit Mode</p>
                <p className="text-lg font-semibold text-emerald-800">{editing ? "On" : "Off"}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Total Pages</p>
                <p className="text-lg font-semibold text-cyan-800">{totalPages}</p>
              </div>
            </div>
          </div>
        </section>

        {/* === FORM === */}
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px] mb-8">
        <form
          onSubmit={handleSubmit}
          className="bg-white p-4 sm:p-6 rounded-2xl border border-slate-200 shadow-md mb-0"
        >
          <div className="grid md:grid-cols-2 gap-4">
          {[
            { name: "name", placeholder: "NAME", required: true },
            { name: "relation", placeholder: "RELATION" },
            { name: "dob", type: "date" },
            {
              name: "gender",
              type: "select",
              options: ["Male", "Female", "Other"],
            },
            // ✅ AADHAAR FIELD WITH ERROR
            { name: "aadhaar_number", placeholder: "AADHAAR NUMBER (12 digits)", required: true },
            { name: "mobile", placeholder: "MOBILE" },
            // ✅ EMAIL FIELD WITH ERROR
            { name: "email", type: "email", placeholder: "EMAIL" },
            { name: "pincode", placeholder: "PINCODE" },
            { name: "postoffice_ui", type: "postoffice" },
            { name: "mandal", placeholder: "MANDAL" },
            { name: "district", placeholder: "DISTRICT" },
            { name: "address", placeholder: "ADDRESS" },
            { name: "state", placeholder: "STATE" },
            { name: "club", placeholder: "CLUB" },
            { name: "coach", placeholder: "COACH" },
          ].map((f) => {
            if (f.type === "select") {
              return (
                <SearchableSelect
                  key={f.name}
                  name={f.name}
                  value={formData[f.name]}
                  onChange={handleChange}
                  required={f.required}
                  className="border p-2 rounded"
                >
                  <option value="">Select Gender</option>
                  {f.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </SearchableSelect>
              );
            }

            // ✅ AADHAAR INPUT WITH ERROR DISPLAY
            if (f.name === "aadhaar_number") {
              return (
                <div key="aadhaar_wrapper" className="space-y-1">
                  <input
                    type="text"
                    name="aadhaar_number"
                    value={formData.aadhaar_number}
                    onChange={handleChange}
                    onBlur={handleAadhaarBlur}
                    placeholder="AADHAAR NUMBER (12 digits)"
                    required
                    className={`border p-2 rounded w-full transition-all ${
                      aadhaarError 
                        ? "border-red-500 bg-red-50 ring-2 ring-red-200" 
                        : "border-gray-300 hover:border-blue-300"
                    }`}
                    maxLength="12"
                  />
                  {aadhaarError && (
                    <div className="text-red-600 text-xs bg-red-50 border border-red-200 p-2 rounded-md shadow-sm">
                      {aadhaarError}
                    </div>
                  )}
                </div>
              );
            }

            // ✅ EMAIL INPUT WITH ERROR DISPLAY
            if (f.name === "email") {
              return (
                <div key="email_wrapper" className="space-y-1">
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleEmailBlur}
                    placeholder="EMAIL"
                    className={`border p-2 rounded w-full transition-all ${
                      emailError 
                        ? "border-red-500 bg-red-50 ring-2 ring-red-200" 
                        : "border-gray-300 hover:border-blue-300"
                    }`}
                  />
                  {emailError && (
                    <div className="text-red-600 text-xs bg-red-50 border border-red-200 p-2 rounded-md shadow-sm">
                      {emailError}
                    </div>
                  )}
                </div>
              );
            }

            // Other fields unchanged
            if (f.type === "postoffice") {
              return (
                <SearchableSelect
                  ref={villageRef}
                  key="postoffice_ui"
                  value={selectedPostOffice}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedPostOffice(val);
                    const selected = pincodeOptions.find((x) => String(x.postoffice) === String(val));
                    setFormData((prev) => ({ ...prev, postoffice: val }));
                    if (!selected) return;
                    setFormData((prev) => ({
                      ...prev,
                      postoffice: val,
                      mandal: selected.mandal || "",
                      district: selected.district || "",
                      state: selected.state || prev.state || "",
                    }));
                  }}
                  disabled={pincodeOptions.length === 0}
                  className="border p-2 rounded"
                >
                  <option value="">
                    {pincodeOptions.length ? "Select Village / Post Office" : "Enter 6-digit pincode first"}
                  </option>
                  {pincodeOptions.map((opt, idx) => (
                    <option key={`${opt.postoffice}-${idx}`} value={opt.postoffice}>
                      {opt.postoffice}
                    </option>
                  ))}
                </SearchableSelect>
              );
            }

            if (f.name === "mandal" || f.name === "district") {
              const isMandal = f.name === "mandal";
              const listId = isMandal ? "admin-mandal-options" : "admin-district-options";

              return (
                <input
                  key={f.name}
                  type="text"
                  name={f.name}
                  value={formData[f.name]}
                  onChange={handleChange}
                  placeholder={f.placeholder}
                  required={f.required}
                  list={listId}
                  className="border p-2 rounded"
                />
              );
            }

            // Other fields unchanged
            return (
              <input
                key={f.name}
                type={f.type || "text"}
                name={f.name}
                value={formData[f.name]}
                onChange={handleChange}
                placeholder={f.placeholder}
                required={f.required}
                className="border p-2 rounded"
              />
            );
          })}
          <datalist id="admin-mandal-options">
            {mandalOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
          <datalist id="admin-district-options">
            {districtOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </div>

          {/* File Uploads */}
          <div className="grid md:grid-cols-2 gap-4 mt-4">
            {["photo", "aadhaar", "birth_certificate", "sadarem", "others"].map(
              (field) => (
                <div key={field} className="border rounded p-3">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {field.replace("_", " ").toUpperCase()}
                  </label>
                  {editing && existingFiles[field] && (
                    <div className="flex flex-col items-center mb-2">
                      {/\.(jpg|jpeg|png)$/i.test(existingFiles[field]) ? (
                        <img
                          src={resolveAssetUrl(existingFiles[field])}
                          alt={field}
                          className="w-20 h-20 object-cover rounded border mb-1 cursor-pointer"
                          onClick={() =>
                            setViewFile(resolveAssetUrl(existingFiles[field]))
                          }
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() =>
                            setViewFile(resolveAssetUrl(existingFiles[field]))
                          }
                          className="text-blue-600 text-xs hover:underline"
                        >
                          View Current {field}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleFileDelete(editId, field)}
                        className="text-red-600 text-xs hover:underline"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                  <input
                    type="file"
                    name={field}
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                    className="border p-1 rounded w-full text-sm"
                  />
                </div>
              )
            )}
          </div>

          <div className="flex justify-end gap-3 mt-4">
            {editing && (
              <button
                type="button"
                onClick={async () => {
                  const confirmed = await confirmAlert({
                    title: "Cancel Edit?",
                    text: "Unsaved changes will be lost",
                    confirmText: "Yes, Cancel",
                  });

                  if (!confirmed) return;

                  setEditing(false);
                  setEditId(null);
                  setFormData(emptyForm);
                  setFileData({});
                  setPincodeOptions([]);
                  setSelectedPostOffice("");
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel Edit
              </button>
            )}
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
            >
              {editing ? "Update Player" : "Add Player"}
            </button>
          </div>
        </form>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">Checklist</p>
            <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
              <li>Validate Aadhaar and email before saving.</li>
              <li>Keep district/mandal mapped to pincode records.</li>
              <li>Upload clear identity documents for approval.</li>
              <li>Use approval + login status actions together.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-800">Tip</p>
            <p className="mt-1 text-sm text-amber-900">
              Use search first, then bulk review approvals on the current page.
            </p>
          </div>
        </aside>
        </div>

        {/* === Search & Table === */}
        <section className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm">
          <SearchBar
            placeholder="Search by Any Column..."
            value={searchTerm}
            onChange={setSearchTerm}
          />
          <ExportButtons table="players" searchTerm={searchTerm} />
          <DataTable
            columns={columns}
            data={players}
            page={page}
            totalPages={totalPages}
            setPage={setPage}
          />
        </section>
      </div>

      {/* === View Player Modal === */}
      <Modal
        isOpen={!!viewPlayer}
        onClose={() => setViewPlayer(null)}
        title={viewPlayer?.name}
        width="600px"
      >
        {viewPlayer && (
          <>
            <div className="space-y-2 text-sm">
              {[
                "relation",
                "gender",
                "dob",
                "aadhaar_number",
                "mobile",
                "email",
                "district",
                "mandal",
                "state",
                "pincode",
                "address",
                "club",
                "coach",
              ].map((key) => (
                <p key={key}>
                  <strong>{key.replace("_", " ")}:</strong>{" "}
                  {viewPlayer[key] || "-"}
                </p>
              ))}
              <p>
                <strong>Approval Status:</strong> {viewPlayer.approval_status || "Approved"}
              </p>
              <p>
                <strong>Login Status:</strong> {toBool(viewPlayer.is_blocked) ? "Blocked" : "Active"}
              </p>
              <p>
                <strong>Block Reason:</strong> {viewPlayer.blocked_reason || "-"}
              </p>
              <p>
                <strong>Event Fee Exemption:</strong> {toBool(viewPlayer.is_fee_exempt) ? "Enabled" : "Disabled"}
              </p>
            </div>

            <div className="mt-4">
              <h3 className="font-semibold mb-2">Documents</h3>
              <div className="grid grid-cols-2 gap-3 text-center">
                {["photo", "aadhaar", "birth_certificate", "sadarem", "others"].map(
                  (file) => (
                    <div key={file}>
                      {viewPlayer[file] ? (
                        /\.(jpg|jpeg|png)$/i.test(viewPlayer[file]) ? (
                          <img
                            src={resolveAssetUrl(viewPlayer[file])}
                            alt={file}
                            className="w-24 h-24 object-cover rounded mx-auto border"
                          />
                        ) : (
                          <button
                            onClick={() =>
                              setViewFile(resolveAssetUrl(viewPlayer[file]))
                            }
                            className="text-blue-600 text-xs hover:underline"
                          >
                            View {file}
                          </button>
                        )
                      ) : (
                        <span className="text-gray-400 text-xs">
                          No {file}
                        </span>
                      )}
                    </div>
                  )
                )}
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* === PDF Modal === */}
      <Modal
        isOpen={!!viewFile}
        onClose={() => setViewFile(null)}
        width="900px"
      >
        <PDFViewer file={viewFile} />
      </Modal>
    </>
  );
};

export default ManagePlayers;
